import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

/**
 * Smoked Glass Dock — floating, frosted bottom dock for mobile only.
 * Houses two actions:
 *   • Projekt        → /konfigurator (secondary)
 *   • Nora fragen    → opens the Nora concierge panel (primary)
 *
 * Visibility rules:
 *   • mobile only (lg:hidden)
 *   • appears after scrolling past the hero, hides near page bottom
 *   • hidden on /kontakt and /konfigurator (active focused flows)
 *   • hidden whenever the Nora panel is open
 *
 * Coordination with <Nora />:
 *   • dispatches `livinora:open-nora` to request opening the panel
 *   • listens to `livinora:nora-state` ({ open }) to hide itself
 */
export default function MobileStickyCTA() {
  const [scrolled, setScrolled] = useState(false);
  const [noraOpen, setNoraOpen] = useState(false);
  const { pathname } = useLocation();

  // Routes where the dock should never appear (form/active flow contexts)
  // and shop-detail (which has its own context-specific Richtpreis + "Set anfragen" CTA).
  const isHiddenRoute =
    pathname === "/kontakt" ||
    pathname === "/konfigurator" ||
    pathname.startsWith("/shop/");

  useEffect(() => {
    if (isHiddenRoute) {
      setScrolled(false);
      return;
    }
    const onScroll = () => {
      const y = window.scrollY;
      const threshold = window.innerHeight * 0.5;
      const nearBottom =
        window.innerHeight + y > document.documentElement.scrollHeight - 160;
      setScrolled(y > threshold && !nearBottom);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname, isHiddenRoute]);

  // Listen for Nora open/close so we can hide the dock cleanly
  useEffect(() => {
    const onState = (e) => setNoraOpen(Boolean(e.detail?.open));
    window.addEventListener("livinora:nora-state", onState);
    return () => window.removeEventListener("livinora:nora-state", onState);
  }, []);

  const openNora = () => {
    window.dispatchEvent(new CustomEvent("livinora:open-nora"));
  };

  const visible = scrolled && !noraOpen && !isHiddenRoute;

  if (isHiddenRoute) return null;

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-[55] lg:hidden flex justify-center pointer-events-none transition-all duration-500 ease-out ${
        visible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-3 pointer-events-none"
      }`}
      style={{
        paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))",
        paddingLeft: "1rem",
        paddingRight: "1rem",
      }}
      data-testid="mobile-sticky-cta"
      aria-hidden={!visible}
    >
      <div
        className="pointer-events-auto inline-flex items-stretch gap-1 p-1.5 rounded-full border border-white/10 shadow-[0_24px_60px_-22px_rgba(0,0,0,0.65)]"
        style={{
          background: "rgba(18,18,18,0.55)",
          backdropFilter: "blur(18px) saturate(140%)",
          WebkitBackdropFilter: "blur(18px) saturate(140%)",
        }}
        data-testid="smoked-glass-dock"
      >
        <Link
          to="/konfigurator"
          data-testid="dock-projekt-btn"
          className="px-5 py-2.5 inline-flex items-center justify-center text-[11px] uppercase tracking-[0.2em] text-white/85 rounded-full hover:text-white hover:bg-white/10 transition-colors"
        >
          Projekt
        </Link>
        <button
          type="button"
          onClick={openNora}
          data-testid="dock-nora-btn"
          aria-label="Nora — Livinora Concierge öffnen"
          className="px-4 py-2.5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] bg-[color:var(--livinora-paper)] text-[color:var(--livinora-ink)] rounded-full transition-transform duration-300 active:scale-[0.98]"
        >
          <span className="w-5 h-5 inline-flex items-center justify-center rounded-full bg-[color:var(--livinora-ink)] text-[color:var(--livinora-paper)] serif text-[11px] leading-none">
            N
          </span>
          Nora fragen
        </button>
      </div>
    </div>
  );
}
